import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSend, FiAlertCircle, FiCornerUpRight } from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { Spinner } from '../../components/ui/Spinner';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { PainelResultado } from '../../components/layout/PainelResultado';
import { useAuth } from '../../contexts/AuthContext';
import { solicitacoesService } from '../../services/solicitacoes.service';
import { recursoTransitoPortalService } from '../../services/recursoTransito.service';

const ERROS_PT: Record<string, string> = {
  instancias_esgotadas: 'O recurso ao CETRAN já foi protocolado ou as instâncias administrativas foram esgotadas. O caminho seguinte é a esfera judicial.',
  aguardando_resposta: 'Aguarde a decisão da JARI antes de recorrer ao CETRAN.',
  documento_nao_e_recurso_transito: 'Este pedido não é um Recurso de Trânsito.',
  sem_acesso: 'Você não tem acesso a este pedido.',
  nao_autenticado: 'Sessão expirada. Faça login novamente.',
  justificativa_curta: 'A justificativa precisa de no mínimo 20 caracteres.',
  tipo_nao_recorrivel: 'Apenas recursos de multa podem ser escalados ao CETRAN. Defesa prévia e indicação de condutor não têm 2ª instância administrativa.',
};

export function RecursoTransitoCetranPage() {
  const { numero } = useParams<{ numero: string }>();
  const { cidadao } = useAuth();
  const navigate = useNavigate();

  const [doc, setDoc] = useState<{ id: string; numero: string; titulo: string; status: string; campos_customizados: Record<string, unknown> } | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [justificativa, setJustificativa] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState<{ numero: string; instancia: number } | null>(null);

  useEffect(() => {
    if (!cidadao || !numero) return;
    solicitacoesService.buscarPorNumero(cidadao.id, numero)
      .then((d) => {
        if (!d) { setErro('Pedido não encontrado.'); return; }
        setDoc(d as never);
      })
      .catch((e) => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setCarregando(false));
  }, [cidadao, numero]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!doc) return;
    if (justificativa.trim().length < 20) {
      setErro('A justificativa do recurso CETRAN é obrigatória (mínimo 20 caracteres).');
      return;
    }
    setErro(null);
    setEnviando(true);
    try {
      const r = await recursoTransitoPortalService.recorrerCetran(doc.id, justificativa.trim());
      setSucesso({ numero: r.numero, instancia: r.instancia });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Falha';
      setErro(ERROS_PT[msg] ?? msg);
    } finally {
      setEnviando(false);
    }
  }

  if (carregando) {
    return (
      <PortalPage titulo="Recurso ao CETRAN" icone={<FiCornerUpRight size={26} />}>
        <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
      </PortalPage>
    );
  }

  if (erro && !doc) {
    return (
      <PainelResultado
        titulo="Pedido não disponível"
        descricao={erro}
        tom="aviso"
        icone={<FiAlertCircle size={32} />}
        acoes={
          <Button variant="outline" onClick={() => navigate('/minhas-solicitacoes')}>
            <FiArrowLeft size={14} /> Voltar
          </Button>
        }
      />
    );
  }

  if (sucesso) {
    return (
      <PainelResultado
        titulo="Recurso CETRAN protocolado"
        descricao={<>Protocolo <strong className="text-slate-900">{sucesso.numero}</strong> · 2ª instância (CETRAN)</>}
        acoes={<Button onClick={() => navigate(`/solicitacao/${sucesso.numero}`)}>Acompanhar recurso</Button>}
      >
        <Alert variante="info">
          O Conselho Estadual de Trânsito analisará seu recurso. Esta é a última instância
          administrativa — em caso de novo indeferimento, o caminho seguinte é a via judicial.
        </Alert>
      </PainelResultado>
    );
  }

  if (!doc) return null;

  const instanciaAtual = (doc.campos_customizados?.['instancia_recurso'] as number | undefined) ?? 0;
  const proximaInstancia = instanciaAtual + 1;
  const tipoPedido = doc.campos_customizados?.['tipo_pedido_rt'] as string | undefined;
  const podeRecorrer = proximaInstancia <= 2 && tipoPedido === 'recurso_multa';

  return (
    <PortalPage
      titulo={podeRecorrer ? 'Recurso ao CETRAN (2ª instância)' : 'Recurso indisponível'}
      subtitulo={`Pedido original: ${doc.numero} · ${doc.titulo}`}
      icone={<FiCornerUpRight size={26} />}
      voltarHref={`/solicitacao/${doc.numero}`}
      voltarLabel="Voltar ao pedido"
    >
      {!podeRecorrer ? (
        <SectionCard>
          <div className="text-center py-8">
            <FiAlertCircle className="mx-auto text-amber-500 mb-3" size={40} />
            <p className="text-sm text-slate-700">
              {tipoPedido !== 'recurso_multa'
                ? 'Apenas recursos de multa indeferidos pela JARI podem ser escalados ao CETRAN.'
                : 'O recurso ao CETRAN já foi protocolado anteriormente. Você já esgotou as instâncias administrativas.'}
            </p>
          </div>
        </SectionCard>
      ) : (
        <div className="space-y-5">
          <Alert variante="info">
            <strong>2ª instância — CETRAN.</strong> Conselho Estadual de Trânsito.
            Esta é a última instância administrativa antes da via judicial.
          </Alert>

          <form onSubmit={handleSubmit} className="space-y-5">
            {erro && <Alert variante="erro">{erro}</Alert>}

            <SectionCard titulo="Justificativa do recurso ao CETRAN" icone={<FiCornerUpRight size={18} />}>
              <p className="text-xs text-slate-500 mb-2">
                Explique por que você discorda da decisão da JARI. Cite os argumentos
                legais que considera não terem sido devidamente apreciados em 1ª instância.
              </p>
              <textarea
                id="just"
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                rows={8}
                required
                placeholder="Descreva detalhadamente os motivos do seu recurso ao CETRAN..."
                className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:border-institutional focus:ring-2 focus:ring-institutional resize-y"
              />
            </SectionCard>

            <div className="flex items-center justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate(`/solicitacao/${doc.numero}`)}>
                Cancelar
              </Button>
              <Button type="submit" loading={enviando}>
                <FiSend size={14} /> Protocolar recurso CETRAN
              </Button>
            </div>
          </form>
        </div>
      )}
    </PortalPage>
  );
}
